// src/mobile_pages/system/User_pcp.jsx
import { useEffect, useMemo, useState } from "react";
import { listCompanies } from "../../api/users/companies";
import {
  listRoles,
  createRole,
  updateRole,
  deleteRole,
} from "../../api/users/roles";
import { listPCP, deletePCP } from "../../api/users/pcp";
import {
  Users,
  Plus,
  ChevronDown,
  ChevronUp,
  Building2,
  Calendar,
  Shield,
} from "lucide-react";

const EMPTY_ROLE = { code: "", name: "" };

const User_pcp = () => {
  const projectId = useMemo(() => localStorage.getItem("projectId"), []);
  const [loading, setLoading] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [pcpRows, setPcpRows] = useState([]);

  const [roleForm, setRoleForm] = useState({ ...EMPTY_ROLE, id: null });
  const isEditingRole = roleForm.id !== null;

  const [showRoleForm, setShowRoleForm] = useState(false);
  const [showRoleList, setShowRoleList] = useState(true);
  const [showPcpList, setShowPcpList] = useState(true);

  // Preset roles
  const PRESET_ROLES = ["발주처", "CM/감리단", "설계사", "시공사"];
  const CUSTOM_LABEL = "직접 입력";
  const ROLE_CODE_MAP = {
    발주처: "OWN",
    "CM/감리단": "CM",
    설계사: "DES",
    시공사: "CON",
  };

  const [selectValue, setSelectValue] = useState("");

  useEffect(() => {
    loadOptions();
    loadPCP();
  }, [projectId]);

  const loadOptions = async () => {
    const [cRes, rRes] = await Promise.all([
      listCompanies(),
      listRoles({ project: projectId }),
    ]);
    setCompanies(cRes.data?.results ?? cRes.data ?? []);
    setRoles(rRes.data?.results ?? rRes.data ?? []);
  };

  const loadPCP = async () => {
    setLoading(true);
    try {
      const { data } = await listPCP({ project: projectId });
      setPcpRows(data?.results ?? data ?? []);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setRoleForm({ ...EMPTY_ROLE, id: null });
    setSelectValue("");
    setShowRoleForm(false);
  };

  const saveRole = async () => {
    if (!roleForm.name.trim()) {
      alert("역할명을 입력하세요.");
      return;
    }
    const trimmedCode = (roleForm.code || "").trim();
    if (!isEditingRole && trimmedCode) {
      const exists = roles.some((r) => (r.code || "").trim() === trimmedCode);
      if (exists) {
        alert("이미 사용 중인 역할 코드입니다.");
        return;
      }
    }
    try {
      if (isEditingRole) {
        await updateRole(roleForm.id, {
          code: roleForm.code || null,
          name: roleForm.name,
        });
        alert("역할이 수정되었습니다.");
      } else {
        await createRole({
          project: projectId,
          code: roleForm.code || null,
          name: roleForm.name,
        });
        alert("역할이 등록되었습니다.");
      }
      resetForm();
      await loadOptions();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data;
      if (status === 400 && msg && (msg.non_field_errors || msg.code)) {
        alert(
          msg.non_field_errors?.[0] ||
            msg.code?.[0] ||
            "이미 존재하는 역할입니다."
        );
      } else {
        alert(`저장 실패(${status}).`);
      }
    }
  };

  const editRole = (r) => {
    setRoleForm({ id: r.id, code: r.code ?? "", name: r.name ?? "" });
    setSelectValue(PRESET_ROLES.includes(r.name) ? r.name : CUSTOM_LABEL);
    setShowRoleForm(true);
  };

  const removeRole = async (id) => {
    if (!window.confirm("해당 역할을 삭제하시겠습니까?")) return;
    try {
      await deleteRole(id);
      await Promise.all([loadOptions(), loadPCP()]);
      alert("역할이 삭제되었습니다.");
    } catch {
      alert("역할 삭제 실패 (매핑되어 있으면 삭제 불가)");
    }
  };

  const removePcp = async (id) => {
    if (!window.confirm("이 참여사 매핑을 삭제하시겠습니까?")) return;
    try {
      await deletePCP(id);
      await loadPCP();
      alert("참여사가 삭제되었습니다.");
    } catch {
      alert("삭제 실패");
    }
  };

  return (
    <div className="mobile-page-root">
      <div className="mobile-page-root-inner space-y-3 mobile-bottom-spacing">
        {/* Header */}
        <div className="bg-white rounded-lg p-3 border">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            <div>
              <h2 className="text-base font-bold text-gray-900">
                참여주체 관리
              </h2>
              <p className="text-[10px] text-gray-500">역할 및 회사 매핑</p>
            </div>
          </div>
        </div>

        {!projectId && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-amber-800 text-sm">
            현재 프로젝트가 선택되지 않았습니다.
          </div>
        )}

        {/* Role Management Section */}
        <div className="section-card overflow-hidden">
          {/* Section Header */}
          <button
            onClick={() => setShowRoleList(!showRoleList)}
            className="w-full flex items-center justify-between p-3 bg-gray-50"
          >
            <span className="text-sm font-semibold">역할 관리</span>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRoleForm(!showRoleForm);
                }}
                className="btn-create-sm text-xs flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                등록
              </button>
              {showRoleList ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </div>
          </button>

          {/* Role Add/Edit Form */}
          {showRoleForm && (
            <div className="p-3 border-t space-y-3 animate-fade-in-soft">
              <div className="flex gap-2">
                <select
                  value={selectValue}
                  onChange={(e) => {
                    const selected = e.target.value;
                    setSelectValue(selected);
                    if (selected === CUSTOM_LABEL) {
                      setRoleForm((v) => ({ ...v, name: "", code: "" }));
                    } else if (PRESET_ROLES.includes(selected)) {
                      setRoleForm((v) => ({
                        ...v,
                        name: selected,
                        code: ROLE_CODE_MAP[selected] || "",
                      }));
                    } else {
                      setRoleForm((v) => ({ ...v, name: "" }));
                    }
                  }}
                  className="flex-1 border rounded-md px-3 py-2 text-sm bg-white"
                >
                  <option value="" disabled>
                    역할명 선택 *
                  </option>
                  {PRESET_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                  <option value={CUSTOM_LABEL}>{CUSTOM_LABEL}</option>
                </select>
              </div>

              {selectValue === CUSTOM_LABEL && (
                <input
                  type="text"
                  placeholder="역할명 *"
                  value={roleForm.name}
                  onChange={(e) =>
                    setRoleForm((v) => ({ ...v, name: e.target.value }))
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
              )}

              <input
                type="text"
                placeholder={
                  roleForm.name
                    ? "코드(선택) 예: CM, OWN"
                    : "역할을 먼저 선택해 주세요"
                }
                value={roleForm.code}
                onChange={(e) =>
                  setRoleForm((v) => ({ ...v, code: e.target.value }))
                }
                readOnly={!roleForm.name}
                className="w-full border rounded-md px-3 py-2 text-sm"
              />

              <div className="flex gap-2">
                <button
                  onClick={resetForm}
                  className="btn-cancel-sm flex-1 text-xs"
                >
                  취소
                </button>
                <button
                  onClick={saveRole}
                  className={`${
                    isEditingRole ? "btn-save-sm" : "btn-create-sm"
                  } flex-1 text-xs`}
                >
                  {isEditingRole ? "수정" : "등록"}
                </button>
              </div>
            </div>
          )}

          {/* Role List */}
          {showRoleList && (
            <div className="p-3 border-t space-y-2">
              {roles.length === 0 ? (
                <p className="text-center text-sm text-gray-500 py-4">
                  역할이 없습니다.
                </p>
              ) : (
                roles.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between py-2 border-b last:border-b-0"
                  >
                    <div>
                      <p className="text-sm font-medium">{r.name}</p>
                      <p className="text-xs text-gray-400">
                        코드: {r.code ?? "-"}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => editRole(r)}
                        className="btn-edit-sm text-xs"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => removeRole(r.id)}
                        className="btn-delete-sm text-xs"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* PCP List Section */}
        <div className="section-card overflow-hidden">
          <button
            onClick={() => setShowPcpList(!showPcpList)}
            className="w-full flex items-center justify-between p-3 bg-gray-50"
          >
            <span className="text-sm font-semibold">참여사 목록</span>
            {showPcpList ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </button>

          {showPcpList && (
            <div className="p-3 border-t space-y-2">
              {loading ? (
                <p className="text-center text-sm text-gray-500 py-4">
                  불러오는 중...
                </p>
              ) : pcpRows.length === 0 ? (
                <p className="text-center text-sm text-gray-500 py-4">
                  데이터 없음
                </p>
              ) : (
                pcpRows.map((row) => {
                  const comp =
                    row.company_name ?? row.company?.name ?? row.company;
                  const role =
                    row.role_name ??
                    row.participant_role?.name ??
                    row.participant_role;
                  const period = `${row.start_date ?? "-"} ~ ${
                    row.end_date ?? "-"
                  }`;

                  return (
                    <div key={row.id} className="section-card p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 mb-1">
                            <Building2 className="w-3 h-3 text-gray-400" />
                            <span className="text-sm font-medium truncate">
                              {comp}
                            </span>
                          </div>
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1">
                              <Shield className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-600">
                                {role}
                              </span>
                              {row.is_lead && (
                                <span className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded">
                                  주관
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-500">
                                {period}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => removePcp(row.id)}
                          className="btn-delete-sm text-xs flex-shrink-0"
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default User_pcp;
